import React from 'react';
import { AlignLeft, AlignCenter, AlignRight, Layers, Trash2 } from 'lucide-react';

const DesignProperties = ({ selectedObject, onPropertyChange }) => {
  if (!selectedObject) {
    return (
      <div className="w-80 bg-white border-l border-lux-border p-12 flex flex-col items-center justify-center text-center">
        <Layers size={48} className="text-lux-border mb-6 stroke-[1px]" />
        <p className="text-[10px] uppercase tracking-widest leading-relaxed text-lux-secondary">Select an element to<br />customize properties</p>
      </div>
    );
  }

  const isText = selectedObject.type === 'i-text' || selectedObject.type === 'text';
  const isImage = selectedObject.type === 'image';

  return (
    <div className="w-80 bg-white border-l border-lux-border flex flex-col overflow-y-auto z-20 shadow-[-10px_0_30px_rgba(0,0,0,0.02)]">
      <div className="p-8 border-b border-lux-border bg-lux-bg/30">
        <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-lux-primary mb-1">Properties</h2>
        <span className="text-[9px] text-lux-gold font-bold uppercase tracking-tighter tabular-nums px-2 py-0.5 bg-lux-gold/10 rounded">{selectedObject.type}</span>
      </div>

      <div className="p-8 space-y-10">
        {/* Color Picker */}
        <div>
          <label className="block text-[9px] uppercase font-bold text-lux-secondary mb-4 tracking-[0.2em]">Appearance</label>
          <div className="flex items-center gap-4 bg-lux-bg p-3 rounded-xl border border-lux-border/50">
            <div
              className="w-12 h-12 rounded-lg border-2 border-white shadow-xl cursor-pointer transition-transform hover:scale-105 active:scale-95"
              style={{ backgroundColor: selectedObject.fill }}
              onClick={() => document.getElementById('fillColor').click()}
            />
            <input
              id="fillColor"
              type="color"
              value={selectedObject.fill || '#000000'}
              onChange={(e) => onPropertyChange('fill', e.target.value)}
              className="hidden"
            />
            <span className="text-xs font-mono uppercase text-lux-primary">{selectedObject.fill}</span>
          </div>
        </div>

        {/* Text Specific Properties */}
        {isText && (
          <div className="space-y-8 pt-2">
            <div>
              <label className="block text-[9px] uppercase font-bold text-lux-secondary mb-5 tracking-[0.2em]">Typography</label>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] uppercase tracking-wider text-lux-primary font-medium">Font Size</span>
                    <span className="text-xs font-bold text-lux-gold tabular-nums">{selectedObject.fontSize}px</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="200"
                    value={selectedObject.fontSize}
                    onChange={(e) => onPropertyChange('fontSize', parseInt(e.target.value))}
                    className="w-full h-1 bg-lux-border rounded-lg appearance-none cursor-pointer accent-lux-gold"
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => onPropertyChange('textAlign', 'left')}
                    className={`p-2 rounded flex justify-center hover:bg-lux-bg transition-colors ${selectedObject.textAlign === 'left' ? 'bg-lux-bg text-lux-gold shadow-sm' : 'text-lux-secondary'}`}
                  >
                    <AlignLeft size={18} />
                  </button>
                  <button
                    onClick={() => onPropertyChange('textAlign', 'center')}
                    className={`p-2 rounded flex justify-center hover:bg-lux-bg transition-colors ${selectedObject.textAlign === 'center' ? 'bg-lux-bg text-lux-gold shadow-sm' : 'text-lux-secondary'}`}
                  >
                    <AlignCenter size={18} />
                  </button>
                  <button
                    onClick={() => onPropertyChange('textAlign', 'right')}
                    className={`p-2 rounded flex justify-center hover:bg-lux-bg transition-colors ${selectedObject.textAlign === 'right' ? 'bg-lux-bg text-lux-gold shadow-sm' : 'text-lux-secondary'}`}
                  >
                    <AlignRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Enhancements */}
        {isImage && (
          <div className="space-y-8 pt-2">
            <div>
              <label className="block text-[9px] uppercase font-bold text-lux-secondary mb-5 tracking-[0.2em]">Enhancements</label>
              <div className="space-y-6">
                {['brightness', 'contrast', 'saturation', 'blur'].map((filter) => {
                  const activeFilter = selectedObject.filters?.find(f => f[filter] !== undefined);
                  const currentValue = activeFilter ? activeFilter[filter] : (filter === 'blur' ? 0 : 0);

                  return (
                    <div key={filter}>
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-[10px] uppercase tracking-wider text-lux-primary font-medium capitalize">{filter}</span>
                        <span className="text-[10px] font-bold text-lux-gold tabular-nums">{currentValue}</span>
                      </div>
                      <input
                        type="range"
                        min={filter === 'blur' ? "0" : "-0.5"}
                        max={filter === 'blur' ? "20" : "0.5"}
                        step="0.01"
                        value={currentValue}
                        onChange={(e) => onPropertyChange(filter, parseFloat(e.target.value))}
                        className="w-full h-1 bg-lux-border rounded-lg appearance-none cursor-pointer accent-lux-gold"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Common Properties */}
        <div>
          <label className="block text-[10px] uppercase font-medium text-lux-secondary mb-3 tracking-widest">Opacity</label>
          <div className="flex justify-between items-center mb-2">
            <span className="text-[11px] text-lux-primary">Transparency</span>
            <span className="text-[11px] font-medium text-lux-gold">{Math.round(selectedObject.opacity * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={selectedObject.opacity}
            onChange={(e) => onPropertyChange('opacity', parseFloat(e.target.value))}
            className="w-full h-1 bg-lux-border rounded-lg appearance-none cursor-pointer accent-lux-gold"
          />
        </div>

        {/* Position/Scale info (read-only for polished look) */}
        <div className="pt-6 border-t border-lux-border grid grid-cols-2 gap-4">
          <div>
            <span className="block text-[9px] uppercase text-lux-secondary mb-1">Width</span>
            <span className="text-xs text-lux-primary">{Math.round(selectedObject.width * selectedObject.scaleX)}px</span>
          </div>
          <div>
            <span className="block text-[9px] uppercase text-lux-secondary mb-1">Height</span>
            <span className="text-xs text-lux-primary">{Math.round(selectedObject.height * selectedObject.scaleY)}px</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-8 border-t border-lux-border mt-auto">
        <button
          onClick={() => onPropertyChange('delete', true)}
          className="w-full py-4 rounded-xl border-2 border-red-100 text-red-500 text-[10px] uppercase tracking-widest font-bold hover:bg-red-50 transition-all flex items-center justify-center gap-2"
        >
          <Trash2 size={14} />
          Delete Element
        </button>
      </div>
    </div>
  );
};

export default DesignProperties;
